import { isNullable } from "@krainovsd/js-helpers";
import { mockRequest } from "@/entities/mock";
import { apiRequest } from "@/api";
import { ENDPOINTS } from "@/api/endpoints";
import { getPeriodRange } from "./report.helpers";
import type {
  ReportIndicators,
  ReportIndicatorsRequest,
  ReportResponse,
  ReportUpdateOrCreateBody,
  RequestStatisticsBody,
  SavedFilter,
} from "./report.types";

const MOCK_INDICATORS: ReportIndicators = {
  sending: 99887,
  clicks: 4521,
  orders: 1023,
  income: 1875400,
  ctr: 4.53,
};

export async function fetchReportIndicators(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _body: ReportIndicatorsRequest,
): Promise<ReportIndicators> {
  return mockRequest<ReportIndicators>(MOCK_INDICATORS);
}

export async function fetchReport(body: RequestStatisticsBody): Promise<ReportResponse> {
  const { period, ...rest } = body;
  const requestBody: RequestStatisticsBody = !isNullable(period)
    ? { ...rest, ...getPeriodRange(period) }
    : rest;

  const [data, error] = await apiRequest<ReportResponse>({
    method: "POST",
    path: ENDPOINTS.getStatistics,
    body: requestBody,
  });
  if (error) throw new Error(error);
  return data;
}

/**
 * Trigger an XLSX export of the current report. The helper's `download: true`
 * flag does the browser-side work — it reads the response as a Blob, extracts
 * the filename from `Content-Disposition` and mime type from `Content-Type`,
 * and kicks off the actual download via an object URL.
 */
export async function downloadReportFile(body: RequestStatisticsBody): Promise<void> {
  const { period, ...rest } = body;
  const requestBody: RequestStatisticsBody = !isNullable(period)
    ? { ...rest, ...getPeriodRange(period) }
    : rest;

  const [, error] = await apiRequest<Blob>({
    method: "POST",
    path: ENDPOINTS.getStatisticsFile,
    body: requestBody,
    download: true,
  });
  if (error) throw new Error(error);
}

export async function fetchReportFiltersById(id: number | string): Promise<SavedFilter> {
  const [data, error] = await apiRequest<SavedFilter>({
    method: "GET",
    path: ENDPOINTS.getReportById(id),
  });
  if (error) throw new Error(error);
  return data;
}

export async function createReport(body: ReportUpdateOrCreateBody): Promise<{ id: number }> {
  const [data, error] = await apiRequest<{ id: number }>({
    method: "POST",
    path: ENDPOINTS.createReport,
    body,
  });
  if (error) throw new Error(error);
  return data;
}

export async function updateReport(
  id: number | string,
  body: ReportUpdateOrCreateBody,
): Promise<void> {
  const [, error] = await apiRequest<unknown>({
    method: "PUT",
    path: ENDPOINTS.updateReport(id),
    body,
  });
  if (error) throw new Error(error);
}
